<template>
  <v-navigation-drawer expand-on-hover permanent rail app v-model:rail="isRail" color="primary" class="menu-drawer"
    :rail-width="70">
    <v-list nav density="comfortable" class="menu-list">

      <!-- BUSCA (visível apenas quando expandido) -->
      <v-slide-y-transition>
        <div class="search-wrapper mb-2">
          <v-text-field v-model="busca" density="compact" variant="outlined" placeholder="Buscar no menu..."
            hide-details clearable class="search-field" @click.stop>
            <template #prepend-inner>
              <font-awesome-icon icon="magnifying-glass" class="search-icon" />
            </template>
          </v-text-field>
        </div>
      </v-slide-y-transition>

      <!-- Sem resultado -->
      <v-list-item v-if="!isRail && busca && menuFiltrado.length === 0" dense>
        <v-list-item-title class="text-caption text-disabled text-center">
          Nenhum item encontrado
        </v-list-item-title>
      </v-list-item>

      <!-- MÓDULOS DINÂMICOS, FILTRADOS POR PERMISSÕES -->
      <template v-for="(mod, idx) in menuFiltrado" :key="mod.title">
        <!-- Se houver subitens, exibe grupo expansível -->
        <template v-if="mod.children?.length || mod.subModules?.length">
          <v-list-item dense clickable @click="toggleGroup(idx)"
            :class="['group-item', { 'active-group': isGroupActive(idx) }]">
            <v-list-item-title>
              <div class="d-flex justify-start module-title-text">
                <v-icon :icon="mod.icon" class="group-icon" />
                <div v-if="!isRail">
                  {{ mod.title }}
                </div>
              </div>
            </v-list-item-title>
            <template #append>
              <v-icon icon="fas fa-chevron-down" class="expand-icon"
                :class="{ rotate: openedGroup === idx || (busca && mod._matchBusca) }" />
            </template>
          </v-list-item>

          <v-expand-transition>
            <div v-show="(openedGroup === idx || (busca && mod._matchBusca)) && !isRail" class="subitem-wrapper">
              <v-list-item v-for="child in mod.children" :key="child.value" dense clickable @click="onChildClick(child)"
                :class="['cursor-pointer', 'subitem', { 'active-item': isActive(child.value), 'subitem-highlight': busca && child._match }]">
                <template #title>
                  <span v-if="busca && child._match" v-html="highlightText(child.title, busca)" />
                  <span v-else>{{ child.title }}</span>
                </template>
              </v-list-item>

              <template v-for="(submod, idx) in mod.subModules" :key="submod.title">
                <div class="sub-module-container">
                  <!-- Se houver subitens, exibe grupo expansível -->
                  <template v-if="submod.children?.length">
                    <v-list-item dense clickable @click="toggleSubGroup(idx, mod)"
                      :class="['group-item', { 'active-group': isSubGroupActive(idx, mod) }]">
                      <v-list-item-title>
                        <div class="d-flex justify-start">
                          <v-icon :icon="submod.icon" class="group-icon" />
                          <div v-if="!isRail">
                            {{ submod.title }}
                          </div>
                        </div>
                      </v-list-item-title>
                      <template #append>
                        <v-icon icon="fas fa-chevron-down" class="expand-icon"
                          :class="{ rotate: openedSubGroup === idx || (busca && submod._matchBusca) }" />
                      </template>
                    </v-list-item>

                    <v-expand-transition>
                      <div v-show="(openedSubGroup === idx || (busca && submod._matchBusca)) && !isRail"
                        class="subitem-wrapper">
                        <v-list-item v-for="child in submod.children" :key="child.value" dense clickable
                          @click="onChildClick(child)"
                          :class="['cursor-pointer', 'subitem', { 'active-item': isActive(child.value), 'subitem-highlight': busca && child._match }]">
                          <template #title>
                            <span v-if="busca && child._match" v-html="highlightText(child.title, busca)" />
                            <span v-else>{{ child.title }}</span>
                          </template>
                        </v-list-item>
                      </div>
                    </v-expand-transition>
                  </template>

                  <!-- Se não tiver subitens, clicável direto para a URL do módulo -->
                  <template v-else>
                    <v-list-item dense clickable @click="onChildClick({ value: submod.value })"
                      :class="['cursor-pointer', { 'active-item': isActive(submod.value) }]">
                      <template #prepend>

                      </template>
                      <v-list-item-title>
                        <div class="d-flex justify-start">
                          <v-icon :icon="submod.icon" class="group-icon" />
                          <div v-if="!isRail">
                            <span v-if="busca && submod._match" v-html="highlightText(submod.title, busca)" />
                            <span v-else>{{ submod.title }}</span>
                          </div>
                        </div>
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                </div>
              </template>

            </div>
          </v-expand-transition>
        </template>

        <!-- Se não tiver subitens, clicável direto para a URL do módulo -->
        <template v-else>
          <v-list-item dense clickable @click="onChildClick({ value: mod.value })"
            :class="['cursor-pointer', { 'active-item': isActive(mod.value) }]">
            <template #prepend>

            </template>
            <v-list-item-title>
              <div class="d-flex justify-start module-title-text">
                <v-icon :icon="mod.icon" class="group-icon" />
                <div v-if="!isRail">
                  <span v-if="busca && mod._match" v-html="highlightText(mod.title, busca)" />
                  <span v-else>{{ mod.title }}</span>
                </div>
              </div>
            </v-list-item-title>
          </v-list-item>
        </template>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserMenu } from '@/composables/useUserMenu'

// Menu dinâmico
const { filteredMenu } = useUserMenu()

const router = useRouter()
const route = useRoute()

// Estado rail e grupos
const isRail = ref(true)
const openedGroup = ref(-1)
const openedSubGroup = ref(-1)

// Verifica se rota é ativa
function isActive(path) {
  return route.path === path || route.path.startsWith(path + '/')
}

// Alterna expansão de grupo
function toggleGroup(idx) {
  openedGroup.value = openedGroup.value === idx ? -1 : idx
  openedSubGroup.value = -1;
}

function toggleSubGroup(idx, mod) {
  openedGroup.value = filteredMenu.value.findIndex(x => x.title == mod.title);
  openedSubGroup.value = openedSubGroup.value === idx ? -1 : idx
}

// Navega e fecha grupo
function onChildClick(item) {
  openedGroup.value = -1
  busca.value = ''
  if (item.value) router.push(item.value)
}

// Índice de grupo ativo
const activeGroupIndex = computed(() =>
  filteredMenu.value.findIndex(mod =>
  (mod.children?.length
    ? mod.children.some(child => isActive(child.value))
    : mod.value === route.path)
  )
)

const activeGroupIndexByRoute = computed(() => {
  let activeRouteModule = filteredMenu.value.filter(x => x.value == route.path)[0];
  if (activeRouteModule)
    return filteredMenu.value.findIndex(x => x.title == activeRouteModule.title);

  filteredMenu.value.forEach(x => {
    if (x.subModules?.length) {
      let matchPath = x.subModules.filter(y => {
        return y.value == route.path
      })[0];
      if (matchPath)
        activeRouteModule = filteredMenu.value.findIndex(y => y.title == x.title);

      if (!activeRouteModule) {
        x.subModules.forEach(y => {
          matchPath = y.children.filter(z => {
            return z.value == route.path
          })[0];
          if (matchPath)
            activeRouteModule = filteredMenu.value.findIndex(z => z.title == x.title);
        })
      }
    }
  })
  return activeRouteModule;
}
)

function activeSubGroupIndex(mod) {
  let result = -1;
  if (mod.subModules?.length) {
    result = mod
      .subModules
      .findIndex(x =>
      (x.children?.length
        ? x.children.some(child => isActive(child.value))
        : x.value === route.path)
      )
  }

  return result;
}

// Verifica se grupo está ativo
function isGroupActive(idx) {
  return openedGroup.value === idx || activeGroupIndex.value === idx || activeGroupIndexByRoute.value === idx;
}

function isSubGroupActive(idx, mod) {
  return openedSubGroup.value === idx || activeSubGroupIndex(mod) === idx;
}

const busca = ref('')

// Limpa busca ao colapsar o drawer
watch(isRail, (val) => { if (val) busca.value = '' })

// Limpa busca ao navegar
watch(() => route.path, () => { busca.value = '' })

// Menu filtrado
const menuFiltrado = computed(() => {
  const termo = busca.value?.trim().toLowerCase()
  if (!termo) return filteredMenu.value
  const resultado = []
  for (const mod of filteredMenu.value) {

    mod.subModules.forEach(subMod => {
      if (!subMod.children?.length) {
        if (subMod.title.toLowerCase().includes(termo))
          resultado.push({ ...subMod, _match: true, _matchBusca: false })
      }
      const filhos = subMod.children.filter(c => c.title.toLowerCase().includes(termo))
      const modMatch = subMod.title.toLowerCase().includes(termo)
      if (filhos.length) {
        resultado.push({
          ...subMod,
          children: modMatch
            ? subMod.children.map(c => ({ ...c, _match: c.title.toLowerCase().includes(termo) }))
            : filhos.map(c => ({ ...c, _match: true })),
          _matchBusca: true,
        })
      } else if (modMatch) {
        resultado.push({ ...subMod, _matchBusca: true })
      }
    });

    if (!mod.children?.length) {
      if (mod.title.toLowerCase().includes(termo))
        resultado.push({ ...mod, _match: true, _matchBusca: false })
      continue
    }
    const filhos = mod.children.filter(c => c.title.toLowerCase().includes(termo))
    const modMatch = mod.title.toLowerCase().includes(termo)
    if (filhos.length) {
      resultado.push({
        ...mod,
        children: modMatch
          ? mod.children.map(c => ({ ...c, _match: c.title.toLowerCase().includes(termo) }))
          : filhos.map(c => ({ ...c, _match: true })),
        _matchBusca: true,
      })
    } else if (modMatch) {
      resultado.push({ ...mod, _matchBusca: true })
    }
  }

  return resultado
})

// Destaca trecho encontrado
function highlightText(texto, termo) {
  const escaped = termo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return texto.replace(new RegExp(`(${escaped})`, 'gi'), '<mark class="menu-highlight">$1</mark>')
}

</script>

<style scoped>
.module-title-text {
  font-size: 14px !important;
  font-weight: 600 !important;
}

.menu-drawer {
  border-right: none;
  background-color: rgb(var(--v-theme-surface, 255, 255, 255)) !important;
  box-shadow: 1px 1px 2px -1px rgba(165, 165, 165, 0.7) !important;
  color: rgb(var(--v-theme-menu, 69, 85, 96)) !important;
}

.menu-list {
  padding-top: 10px;
  margin-top: 10px;
}

.v-list-item {
  min-height: 35px !important;
  padding: 4px 0 !important;
}

.v-list-item:hover {
  background-color: rgba(62, 121, 247, 0.05) !important;
  transition: background-color 0.2s;
}

.group-icon,
.menu-list .v-list-item .v-icon {
  font-size: 16px !important;
  color: rgb(var(--v-theme-menu, 69, 85, 96)) !important;
  opacity: 1 !important;
}

.menu-list .v-list-item-title {
  color: rgb(var(--v-theme-menu, 69, 85, 96)) !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  margin: 10px 0 !important;
}

/* Itens ativos */
.active-item {
  background-color: rgba(62, 121, 247, 0.1) !important;
  border-right: 3px solid rgb(var(--v-theme-primary, 51, 102, 255)) !important;
}

.active-item .v-list-item-title {
  color: rgb(var(--v-theme-primary, 51, 102, 255)) !important;
  font-weight: 600 !important;
}

/* Grupo ativo */
.active-group {
  background-color: rgba(62, 121, 247, 0.1) !important;
  border-right: 3px solid rgb(var(--v-theme-primary, 51, 102, 255)) !important;
}

.subitem {
  color: rgb(var(--v-theme-menu, 69, 85, 96)) !important;
  margin-left: 28px !important;
}

.active-item .bullet-marker {
  color: rgb(var(--v-theme-on-surface, 0, 0, 0)) !important;
  font-weight: bold;
}

.expand-icon.rotate {
  transform: rotate(180deg);
}

.v-list-item--nav {
  padding-inline: 12px !important;
}

.v-navigation-drawer--is-hovering {
  width: 400px !important;
}

.search-wrapper {
  transition: opacity 0.2s;
}

.search-field :deep(.v-field) {
  border-radius: 8px !important;
  font-size: 14px !important;
}

.search-field :deep(.v-field__input) {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
  padding-left: 30px !important;
  min-height: unset !important;
  font-size: 13px !important;
  color: rgb(var(--v-theme-menu, 69, 85, 96)) !important;
}

.search-field :deep(.v-field__outline) {
  color: rgb(255 255 255) !important;
}

:deep(.v-field) {
  background: #198cff24;
  padding: 5px 20px;
}

.search-icon {
  font-size: 15px !important;
  color: rgb(var(--v-theme-menu, 69, 85, 96)) !important;
  opacity: 0.6;
  margin-left: -6px;
}

.subitem-highlight {
  background-color: rgba(62, 121, 247, 0.04) !important;
}

:deep(.menu-highlight) {
  background-color: rgba(62, 121, 247, 0.2);
  color: rgb(var(--v-theme-primary, 51, 102, 255));
  border-radius: 2px;
  padding: 0 1px;
  font-weight: 700;
}

.group-icon {
  margin-right: 12px;
}

.sub-module-container {
  margin-left: 32px;
}
</style>
